import { LocalFile } from '@graphql/generated';

// Platform-agnostic on purpose (Copilot review, PR #5914): videoCompression.ts
// pulls in native-only modules (expo-file-system, react-native-compressor,
// react-native-fs) at module scope, so importing anything from it -- even a
// pure function -- drags those into the web bundle via videoCompression.web.ts
// re-exporting them. These two helpers have no native dependency of their
// own, so they live here and both videoCompression.ts and
// videoCompression.web.ts re-export them from this shared module instead.

// `isVideo` is the primary signal, but it's picker-set metadata, not a
// derived fact -- a picker that mistags a video (e.g. the document picker
// path, fixed in selectFiles.ts, Copilot review PR #5914 round 4) would
// otherwise skip compression and validation for that file entirely. Fall
// back to contentType so a video is still routed correctly even if some
// future/other caller gets the flag wrong.
export const isVideoLike = (localFile: LocalFile): boolean =>
  localFile.isVideo ||
  (localFile.contentType ?? '').toLowerCase().startsWith('video/');

// Shared by selectFiles.ts and ShareIntentToChat.tsx: a document-picker asset
// or a shared attachment can report an empty/generic MIME type (some file
// providers omit it entirely). Without this extension fallback, a video
// picked/shared this way gets isVideo: false and bypasses compression and
// TA-10871/TA-10895's container validation completely -- reproducing the
// exact broken-upload bug this PR exists to close (Copilot review, PR #5914).
// Includes .flv (Copilot review, PR #5914) -- this codebase already
// recognizes video/x-flv elsewhere (RestyleIcon.tsx), so a MIME-less
// clip.flv must be recognized by extension too, not just the mp4/mov family.
// Also covers ogg/mpeg/mp2t (Copilot review, PR #5914) -- kept in sync with
// KNOWN_NON_ISO_BMFF_EXTENSION_PATTERN in videoCompression.ts. This list is
// intentionally bounded to formats realistically seen on a mobile chat app
// (camera/gallery output, or a file shared from another messaging/browser
// app), not an exhaustive registry of every video MIME type that exists.
// Also covers .wmv (Copilot review, PR #5914) -- this codebase already
// treats WMV as a video elsewhere (ImagesPreviewNotLogin.web.tsx, the shared
// icon MIME map).
const VIDEO_EXTENSION_PATTERN =
  /\.(mp4|mov|m4v|3gp|3g2|webm|mkv|avi|flv|ogv|mpeg|mpg|ts|m2ts|wmv)$/i;

// Copilot review, PR #5914: `name?.split('.').pop()` (as selectFiles.ts and
// ShareIntentToChat.tsx used to derive a cached destination filename)
// returns the WHOLE string when there's no dot (e.g. "clip" -> "clip", not
// undefined) -- silently treating an extensionless name as if its extension
// were the whole filename, producing a cached file like "clientId.clip".
// Since isKnownNonIsoBmffVideo later re-derives the container format from
// that cached filename, a lost/wrong extension there defeats detection even
// though isVideoByMimeOrName correctly classified the file as video up
// front. Requires an actual dot-delimited suffix instead.
export const extractFileExtension = (
  value: string | null | undefined
): string | undefined => /\.([a-zA-Z0-9]+)$/.exec(value ?? '')?.[1];

// Accepts multiple name-like candidates (Copilot review, PR #5914): a single
// candidate (e.g. just the picker's `name` field) can itself be missing or
// have no visible extension (a content:// source URI, a share provider that
// omits the filename) even though another candidate for the same file (the
// URI/path) does carry one. Any candidate matching is enough.
export const isVideoByMimeOrName = (
  mimeType: string | null | undefined,
  ...names: (string | null | undefined)[]
): boolean =>
  // Copilot review, PR #5914: 'video' (no slash) also matches a malformed/
  // bogus MIME value like "videotext/plain" -- inconsistent with isVideoLike,
  // which correctly requires the 'video/' prefix. Match that here too, or a
  // non-video document with such a value gets routed through compression and
  // rejected by the MP4-only validator.
  (mimeType ?? '').toLowerCase().startsWith('video/') ||
  names.some((name) => VIDEO_EXTENSION_PATTERN.test(name ?? ''));

/**
 * TA-10932: both compressVideos callers (MyChatForm, ShareIntentToChat) drop
 * unsupported videos silently -- this picks the message telling the user why.
 * Returns null for zero unsupported files so callers can skip alerting.
 */
export const getVideoUnsupportedAlertMessage = (
  unsupportedCount: number,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null => {
  if (unsupportedCount === 0) return null;
  return unsupportedCount === 1
    ? t('models:chat.input.videoUnsupported')
    : t('models:chat.input.videoUnsupportedMultiple', {
        count: unsupportedCount,
      });
};
